import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { buildCsv } from '@/lib/csv';
import { downloadText } from '@/lib/download';
import { cn } from '@/lib/utils';
import awbService, { AWBDetail, SearchAWBRequest } from '@/services/awbService';
import { AWB_STATUS_COLORS, AWB_STATUS_LABELS } from '@/types/awb';
import type { AWBStatus, PaymentMethod } from '@/types/awb';
import { format } from 'date-fns';
import {
    ArrowRight,
    Calendar,
    Download,
    Edit,
    Eye,
    MapPin,
    MoreHorizontal,
    Package,
    Plus,
    Printer,
    RefreshCw,
    Trash2,
    Truck,
    User,
    Weight
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AWBFilters, { AWBFiltersState } from './AWBFilters';
import AWBStats from './AWBStats';

interface AWB {
  id: string;
  awbNumber: string;
  customer: string;
  origin: string;
  destination: string;
  service: string;
  bookingDate: string;
  status: string;
  weight: number;
  pieces: number;
  shipmentValue: number;
  currency: {
    symbol?: string;
    code?: string;
    name?: string;
  } | string;
  paymentMethod: string;
  shipper: {
    name: string;
    city: string;
    country: string;
  };
  consignee: {
    name: string;
    city: string;
    country: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface AWBListProps {}

type FilterLabelKey = 'customer' | 'service' | 'destination';
type FilterLabelMap = Partial<Record<FilterLabelKey, string>>;

const initialFilters: AWBFiltersState = {
  search: '',
  status: 'all',
  customer: '',
  service: '',
  paymentMethod: 'all',
  dateFrom: '',
  dateTo: '',
  destination: '',
  weightMin: '',
  weightMax: ''
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_STORAGE_KEY = 'awb-list-page-size';

const getInitialPageSize = () => {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const stored = window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
  if (!stored) return DEFAULT_PAGE_SIZE;
  const parsed = Number.parseInt(stored, 10);
  return PAGE_SIZE_OPTIONS.includes(parsed as (typeof PAGE_SIZE_OPTIONS)[number])
    ? parsed
    : DEFAULT_PAGE_SIZE;
};

const getCountryName = (country: any): string => {
  if (!country) return 'Unknown';
  if (typeof country === 'string') return country;
  if (typeof country === 'object' && country !== null) {
    return country.name || country.code || 'Unknown';
  }
  return 'Unknown';
};

const getDisplayValue = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null) {
    return value.name || value.code || value._id || '';
  }
  return String(value);
};

const getCurrencySymbol = (currency: any): string => {
  if (!currency) return '₹';
  if (typeof currency === 'string') return '₹';
  if (typeof currency === 'object' && currency !== null) {
    return currency.symbol || currency.code || '₹';
  }
  return '₹';
};

const formatAWBForList = (awb: AWBDetail): AWB => ({
  id: awb.id,
  awbNumber: awb.awbNumber || '',
  customer: getDisplayValue(awb.customer) || 'Unknown Customer',
  origin: getCountryName(awb.origin) || 'Unknown Origin',
  destination: getCountryName(awb.destination) || 'Unknown Destination',
  service: getDisplayValue(awb.service) || 'Unknown Service',
  bookingDate: awb.bookingDate || '',
  status: awb.currentStatus || 'DRAFT',
  weight: awb.totalChargeableWeight || 0,
  pieces: awb.totalPieces || 0,
  shipmentValue: awb.shipmentValue || 0,
  currency: awb.currency || { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  paymentMethod: awb.paymentMethod || 'PREPAID',
  shipper: {
    name: awb.shipper?.personName || awb.shipper?.company || '',
    city: awb.shipper?.city || '',
    country: getCountryName(awb.shipper?.country)
  },
  consignee: {
    name: awb.consignee?.personName || awb.consignee?.company || '',
    city: awb.consignee?.city || '',
    country: getCountryName(awb.consignee?.country)
  },
  createdAt: awb.createdAt || '',
  updatedAt: awb.updatedAt || ''
});

const countActiveFilters = (activeFilters: AWBFiltersState): number => {
  let count = 0;
  if (activeFilters.search.trim()) count += 1;
  if (activeFilters.status !== 'all') count += 1;
  if (activeFilters.customer) count += 1;
  if (activeFilters.service) count += 1;
  if (activeFilters.paymentMethod !== 'all') count += 1;
  if (activeFilters.dateFrom) count += 1;
  if (activeFilters.dateTo) count += 1;
  if (activeFilters.destination) count += 1;
  if (activeFilters.weightMin.trim()) count += 1;
  if (activeFilters.weightMax.trim()) count += 1;
  return count;
};

const AWBList: React.FC<AWBListProps> = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [awbs, setAwbs] = useState<AWBDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(getInitialPageSize);
  const [selectedAWBs, setSelectedAWBs] = useState<string[]>([]);
  const [filters, setFilters] = useState<AWBFiltersState>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<AWBFiltersState>(initialFilters);
  const [filterLabels, setFilterLabels] = useState<FilterLabelMap>({});
  const [appliedFilterLabels, setAppliedFilterLabels] = useState<FilterLabelMap>({});
  const latestRequestRef = React.useRef(0);

  const activeFilterCount = useMemo(() => countActiveFilters(appliedFilters), [appliedFilters]);
  const formattedAwbs = useMemo(() => awbs.map(formatAWBForList), [awbs]);
  const selectedAwbSet = useMemo(() => new Set(selectedAWBs), [selectedAWBs]);
  const hasActiveFilters = activeFilterCount > 0;
  const hasPendingFilters = useMemo(() => {
    const keys = Object.keys(initialFilters) as (keyof AWBFiltersState)[];
    return keys.some((key) => filters[key] !== appliedFilters[key]);
  }, [filters, appliedFilters]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  const loadAWBs = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);
    setSelectedAWBs([]); 
    try {
      const activeFilters = appliedFilters;
      const searchParams: SearchAWBRequest = {
        page: currentPage,
        limit: pageSize,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        responseMode: 'summary'
      };

      if (activeFilters.search.trim()) {
        searchParams.awbNumber = activeFilters.search.trim();
      }
      if (activeFilters.status && activeFilters.status !== 'all') {
        searchParams.currentStatus = activeFilters.status as AWBStatus;
      }
      if (activeFilters.customer) {
        searchParams.customer = activeFilters.customer;
      }
      if (activeFilters.service) {
        searchParams.service = activeFilters.service;
      }
      if (activeFilters.destination) {
        searchParams.destination = activeFilters.destination;
      }
      if (activeFilters.paymentMethod && activeFilters.paymentMethod !== 'all') {
        searchParams.paymentMethod = activeFilters.paymentMethod as PaymentMethod;
      }
      if (activeFilters.dateFrom) {
        searchParams.bookingDateFrom = new Date(activeFilters.dateFrom);
      }
      if (activeFilters.dateTo) {
        searchParams.bookingDateTo = new Date(activeFilters.dateTo);
      }
      if (activeFilters.weightMin.trim()) {
        const weightMin = Number(activeFilters.weightMin);
        if (Number.isFinite(weightMin)) {
          searchParams.weightMin = weightMin;
        }
      }
      if (activeFilters.weightMax.trim()) {
        const weightMax = Number(activeFilters.weightMax);
        if (Number.isFinite(weightMax)) {
          searchParams.weightMax = weightMax;
        }
      }

      const result = await awbService.searchAWBs(searchParams);
      if (latestRequestRef.current !== requestId) return;
      setAwbs(result.awbs);
      setTotalPages(result.totalPages);
      setTotalCount(result.total);
    } catch (error) {
      if (latestRequestRef.current !== requestId) return;
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load AWBs. Please try again.',
        variant: 'destructive',
      });
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [appliedFilters, currentPage, pageSize, toast]);

  useEffect(() => {
    loadAWBs();
  }, [loadAWBs]);

  const handleFilterChange = (key: keyof AWBFiltersState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    if (!hasPendingFilters) return;
    setCurrentPage(1);
    setAppliedFilters(filters);
    setAppliedFilterLabels(filterLabels);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setCurrentPage(1);
    setFilterLabels({});
    setAppliedFilterLabels({});
  };

  const handleFilterLabelChange = (key: keyof AWBFiltersState, label: string) => {
    const labelKey = key as FilterLabelKey;
    if (!['customer', 'service', 'destination'].includes(labelKey)) return;
    setFilterLabels(prev => ({
      ...prev,
      [labelKey]: label
    }));
  };

  const clearAppliedFilters = (keys: (keyof AWBFiltersState)[]) => {
    if (keys.length === 0) return;
    setFilters(prev => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = initialFilters[key];
      });
      return next;
    });
    setAppliedFilters(prev => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = initialFilters[key];
      });
      return next;
    });
    setCurrentPage(1);
    const labelKeys = new Set<FilterLabelKey>(['customer', 'service', 'destination']);
    setFilterLabels(prev => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((key) => {
        const labelKey = key as FilterLabelKey;
        if (labelKeys.has(labelKey) && next[labelKey]) {
          delete next[labelKey];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setAppliedFilterLabels(prev => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((key) => {
        const labelKey = key as FilterLabelKey;
        if (labelKeys.has(labelKey) && next[labelKey]) {
          delete next[labelKey];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAWBs(formattedAwbs.map(a => a.awbNumber));
    } else {
      setSelectedAWBs([]);
    }
  };

  const handleSelectOne = (awbNumber: string, checked: boolean) => {
    setSelectedAWBs(prev => {
      if (checked) {
        return prev.includes(awbNumber) ? prev : [...prev, awbNumber];
      }
      return prev.filter(id => id !== awbNumber);
    });
  };

  const handleBulkDelete = async () => {
    if (selectedAWBs.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedAWBs.length} selected AWBs? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      await Promise.all(selectedAWBs.map(id => awbService.deleteAWB(id)));
      
      toast({
        title: 'Success',
        description: `${selectedAWBs.length} AWBs have been deleted successfully.`,
      });
      await loadAWBs();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete some AWBs. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setSelectedAWBs([]);
    }
  };

  const exportAwbs = (rowsToExport: AWB[], label: string) => {
    try {
      if (rowsToExport.length === 0) {
        toast({ title: "No Data", description: "No AWBs to export." });
        return;
      }
      
      const headers = ['AWB Number', 'Customer', 'Origin', 'Destination', 'Status', 'Date', 'Amount', 'Pieces', 'Weight'];
      const rows = rowsToExport.map(formatted => [
        formatted.awbNumber,
        formatted.customer,
        formatted.origin,
        formatted.destination,
        formatted.status,
        formatted.bookingDate ? new Date(formatted.bookingDate).toLocaleDateString() : '',
        formatted.shipmentValue,
        formatted.pieces,
        formatted.weight
      ]);
      const csvContent = buildCsv(headers, rows, { quoteStrings: true });
      downloadText(
        csvContent,
        `awb_${label}_export_${format(new Date(), 'yyyy-MM-dd')}.csv`,
        'text/csv;charset=utf-8;'
      );
      
      toast({
        title: "Export Successful",
        description: `Exported ${rowsToExport.length} records.`,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "An error occurred while exporting.",
        variant: "destructive"
      });
    }
  };

  const exportToExcel = () => {
    exportAwbs(formattedAwbs, 'page');
  };

  const exportSelectedAwbs = () => {
    const selectedRows = formattedAwbs.filter((awb) => selectedAwbSet.has(awb.awbNumber));
    exportAwbs(selectedRows, 'selected');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleCreateNew = () => {
    navigate('/awb/create');
  };

  const handleView = (awb: AWB) => {
    navigate(`/awb/${awb.awbNumber}/view`);
  };

  const handleEdit = (awb: AWB) => {
    navigate(`/awb/${awb.awbNumber}/edit`);
  };

  const handleDeleteAWB = async (awb: AWB) => {
    if (!window.confirm('Are you sure you want to delete this AWB? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await awbService.deleteAWB(awb.awbNumber);
      toast({
        title: 'Success',
        description: 'AWB has been deleted successfully.',
      });
      await loadAWBs();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete AWB',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLabel = async (awb: AWB) => {
    try {
      toast({ title: "Generating Label", description: "Please wait..." });
      await awbService.generateLabel(awb.awbNumber);
      toast({ title: "Success", description: "Label generated successfully" });
    } catch (e) {
      toast({ title: "Error", description: "Failed to generate label", variant: "destructive" });
    }
  };

  const isLoadingState = loading;
  const hasSelection = selectedAWBs.length > 0;
  const allSelected = formattedAwbs.length > 0 && selectedAWBs.length === formattedAwbs.length;

  // Mobile Card Component
  const AWBCard: React.FC<{ awb: AWB }> = ({ awb }) => {
    const isSelected = selectedAwbSet.has(awb.awbNumber);
    return (
      <Card className={cn("overflow-hidden transition-all", isSelected && "ring-2 ring-primary")}>
        <CardContent className="p-4 space-y-3">
          {/* Header: Checkbox, AWB Number & Status */}
          <div className="flex justify-between items-start gap-2">
            <div className="flex items-center gap-3">
              <Checkbox 
                checked={isSelected}
                onCheckedChange={(checked) => handleSelectOne(awb.awbNumber, !!checked)}
                aria-label={`Select AWB ${awb.awbNumber}`}
              />
              <div 
                className="font-bold text-primary cursor-pointer hover:underline text-base"
                onClick={() => handleView(awb)}
              >
                {awb.awbNumber}
              </div>
            </div>
            <Badge 
              variant="outline" 
              className={cn("shrink-0", AWB_STATUS_COLORS[awb.status as keyof typeof AWB_STATUS_COLORS])}
            >
              {AWB_STATUS_LABELS[awb.status as keyof typeof AWB_STATUS_LABELS] || awb.status}
            </Badge>
          </div>

          {/* Route */}
          <div className="flex items-center gap-2 text-sm bg-muted/40 p-2 rounded-lg">
            <MapPin className="h-4 w-4 text-blue-600 shrink-0" />
            <span className="font-medium truncate">{awb.origin}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
            <MapPin className="h-4 w-4 text-green-600 shrink-0" />
            <span className="font-medium truncate">{awb.destination}</span>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground text-xs">
                <User className="h-3 w-3" /> Customer
              </div>
              <div className="font-medium truncate">{awb.customer}</div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground text-xs">
                <Truck className="h-3 w-3" /> Service
              </div>
              <div className="font-medium truncate">{awb.service}</div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground text-xs">
                <Calendar className="h-3 w-3" /> Booking Date
              </div>
              <div className="font-medium">{awb.bookingDate ? new Date(awb.bookingDate).toLocaleDateString() : '-'}</div>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-muted-foreground text-xs">
                <Weight className="h-3 w-3" /> Weight / Pieces
              </div>
              <div className="font-medium">{awb.weight.toFixed(1)} kg / {awb.pieces} pcs</div>
            </div>
          </div>

          {/* Footer: Value & Actions */}
          <div className="flex items-center justify-between pt-3 border-t">
            <div className="text-lg font-bold">
              {getCurrencySymbol(awb.currency)} {awb.shipmentValue.toFixed(2)}
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" aria-label={`Actions for AWB ${awb.awbNumber}`}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleView(awb)}>
                    <Eye className="mr-2 h-4 w-4" /> View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleEdit(awb)}>
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handlePrintLabel(awb)}>
                    <Printer className="mr-2 h-4 w-4" /> Print Label
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/track?awb=${awb.awbNumber}`)}>
                    <Truck className="mr-2 h-4 w-4" /> Track
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteAWB(awb)}>
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" onClick={() => handleView(awb)}>View</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">AWB Management</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Manage and track your airway bills</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasSelection && (
            <div className="hidden lg:flex items-center gap-2 bg-muted p-1 rounded-md">
              <span className="text-xs px-2 font-medium">{selectedAWBs.length} selected</span>
              <Button variant="outline" size="sm" onClick={exportSelectedAwbs} className="h-8">
                <Download className="h-3 w-3 mr-1" />
                Export
              </Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete} className="h-8">
                <Trash2 className="h-3 w-3 mr-1" />
                Delete
              </Button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => loadAWBs()} disabled={isLoadingState}>
            <RefreshCw className={cn("h-4 w-4 mr-1", isLoadingState && "animate-spin")} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button size="sm" onClick={handleCreateNew}>
            <Plus className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Create AWB</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      <AWBStats awbs={awbs} totalCount={totalCount} />

      <AWBFilters 
        filters={filters} 
        appliedFilters={appliedFilters}
        onFilterChange={handleFilterChange} 
        onClearFilters={clearFilters}
        onApplyFilters={handleApplyFilters}
        onClearAppliedFilters={clearAppliedFilters}
        onFilterLabelChange={handleFilterLabelChange}
        filterLabels={filterLabels}
        appliedFilterLabels={appliedFilterLabels}
        isOpen={isFilterOpen}
        onToggleOpen={() => setIsFilterOpen(!isFilterOpen)}
        totalCount={totalCount}
        loading={isLoadingState}
        activeFilterCount={activeFilterCount}
        hasPendingChanges={hasPendingFilters}
      />

      {/* AWB Table/List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              <span>AWB List ({totalCount})</span>
            </div>
            <div className="flex items-center gap-2">
              <Select value={pageSize.toString()} onValueChange={(value) => handlePageSizeChange(parseInt(value, 10))}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={exportToExcel}>
                <Download className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          {isLoadingState && awbs.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-muted-foreground">Loading AWBs...</p>
              </div>
            </div>
          ) : formattedAwbs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {hasActiveFilters ? 'No Results Found' : 'No AWBs Found'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters
                  ? 'No AWBs match the current filters. Try adjusting or clearing them.'
                  : 'Create your first AWB to get started'}
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-2">
                {hasActiveFilters ? (
                  <>
                    <Button variant="outline" onClick={() => setIsFilterOpen(true)}>
                      Adjust Filters
                    </Button>
                    <Button onClick={clearFilters}>Clear Filters</Button>
                  </>
                ) : (
                  <Button onClick={handleCreateNew}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create AWB
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="w-full">
              {/* Desktop Table View - visible on lg and up */}
              <div className="hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr className="border-b">
                        <th className="p-3 text-left w-10">
                          <Checkbox 
                            checked={allSelected} 
                            onCheckedChange={(checked) => handleSelectAll(!!checked)}
                            aria-label="Select all AWBs"
                          />
                        </th>
                        <th className="p-3 text-left font-semibold whitespace-nowrap">AWB #</th>
                        <th className="p-3 text-left font-semibold whitespace-nowrap">Customer</th>
                        <th className="p-3 text-left font-semibold whitespace-nowrap">Route</th>
                        <th className="p-3 text-left font-semibold whitespace-nowrap">Service</th>
                        <th className="p-3 text-center font-semibold whitespace-nowrap">Status</th>
                        <th className="p-3 text-right font-semibold whitespace-nowrap">Weight</th>
                        <th className="p-3 text-right font-semibold whitespace-nowrap">Pieces</th>
                        <th className="p-3 text-right font-semibold whitespace-nowrap">Value</th>
                        <th className="p-3 text-left font-semibold whitespace-nowrap">Date</th>
                        <th className="p-3 text-center font-semibold w-16">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formattedAwbs.map((awb) => {
                        const isSelected = selectedAwbSet.has(awb.awbNumber);
                        return (
                          <tr 
                            key={awb.id} 
                            className={cn(
                              "border-b hover:bg-muted/30 transition-colors",
                              isSelected && "bg-primary/5"
                            )}
                          >
                            <td className="p-3">
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={(checked) => handleSelectOne(awb.awbNumber, !!checked)}
                                aria-label={`Select AWB ${awb.awbNumber}`}
                              />
                            </td>
                            <td className="p-3">
                              <span 
                                className="font-semibold text-primary hover:underline cursor-pointer"
                                onClick={() => handleView(awb)}
                              >
                                {awb.awbNumber}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="max-w-[150px] truncate" title={awb.customer}>
                                {awb.customer}
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-1 text-xs">
                                <span className="text-blue-600 font-medium truncate max-w-[80px]" title={awb.origin}>{awb.origin}</span>
                                <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                <span className="text-green-600 font-medium truncate max-w-[80px]" title={awb.destination}>{awb.destination}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="max-w-[100px] truncate" title={awb.service}>
                                {awb.service}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <Badge 
                                variant="outline" 
                                className={cn(
                                  "text-xs",
                                  AWB_STATUS_COLORS[awb.status as keyof typeof AWB_STATUS_COLORS]
                                )}
                              >
                                {AWB_STATUS_LABELS[awb.status as keyof typeof AWB_STATUS_LABELS] || awb.status}
                              </Badge>
                            </td>
                            <td className="p-3 text-right font-medium tabular-nums">
                              {awb.weight.toFixed(2)} <span className="text-muted-foreground text-xs">kg</span>
                            </td>
                            <td className="p-3 text-right font-medium tabular-nums">
                              {awb.pieces}
                            </td>
                            <td className="p-3 text-right font-medium tabular-nums">
                              {getCurrencySymbol(awb.currency)} {awb.shipmentValue.toFixed(2)}
                            </td>
                            <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                              {awb.bookingDate ? new Date(awb.bookingDate).toLocaleDateString() : '-'}
                            </td>
                            <td className="p-3 text-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    aria-label={`Actions for AWB ${awb.awbNumber}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuItem onClick={() => handleView(awb)}>
                                    <Eye className="mr-2 h-4 w-4" /> View
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleEdit(awb)}>
                                    <Edit className="mr-2 h-4 w-4" /> Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handlePrintLabel(awb)}>
                                    <Printer className="mr-2 h-4 w-4" /> Print Label
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => navigate(`/track?awb=${awb.awbNumber}`)}>
                                    <Truck className="mr-2 h-4 w-4" /> Track
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteAWB(awb)}>
                                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Mobile/Tablet Card View - visible below lg */}
              <div className={cn("lg:hidden space-y-3 p-4", hasSelection && "pb-24")}>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={allSelected}
                      onCheckedChange={(checked) => handleSelectAll(!!checked)}
                      aria-label="Select all AWBs"
                    />
                    <span>Select all</span>
                  </div>
                  {hasSelection && (
                    <Button variant="ghost" size="sm" onClick={() => setSelectedAWBs([])}>
                      Clear selection
                    </Button>
                  )}
                </div>
                {formattedAwbs.map((awb) => (
                  <AWBCard key={awb.id} awb={awb} />
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || isLoadingState}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || isLoadingState}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {hasSelection && (
        <div className="lg:hidden fixed bottom-4 left-4 right-4 z-40">
          <div className="flex items-center justify-between gap-3 rounded-lg border bg-background/95 p-3 shadow-lg backdrop-blur">
            <span className="text-sm font-medium">{selectedAWBs.length} selected</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedAWBs([])}>
                Clear
              </Button>
              <Button variant="outline" size="sm" onClick={exportSelectedAwbs}>
                Export
              </Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AWBList;
